import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Loader2, ChevronLeft, ShoppingBag } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { useCart } from '../context/CartContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// PayPal Client ID (sandbox for testing)
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || 'sb';

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, cartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    phone: '',
    shipping_address: '',
    shipping_city: '',
    shipping_state: '',
    shipping_zip: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Check for existing order_id in URL (for cancel redirect)
    const params = new URLSearchParams(window.location.search);
    const existingOrderId = params.get('order_id');
    if (existingOrderId) {
      setOrderId(existingOrderId);
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.customer_name.trim()) newErrors.customer_name = 'Name is required';
    if (!formData.customer_email.trim()) newErrors.customer_email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.customer_email)) newErrors.customer_email = 'Invalid email';
    if (!formData.shipping_address.trim()) newErrors.shipping_address = 'Address is required';
    if (!formData.shipping_city.trim()) newErrors.shipping_city = 'City is required';
    if (!formData.shipping_state.trim()) newErrors.shipping_state = 'State is required';
    if (!formData.shipping_zip.trim()) newErrors.shipping_zip = 'ZIP code is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const createOrder = async () => {
    if (!validateForm()) return null;

    try {
      const response = await axios.post(`${API}/orders`, {
        items: cartItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        })),
        ...formData,
        customer_email: formData.customer_email.toLowerCase()
      });
      setOrderId(response.data.id);
      return response.data.id;
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to create order. Please try again.');
      return null;
    }
  };

  const handleStripeCheckout = async () => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setLoading(true);
    try {
      let currentOrderId = orderId;
      if (!currentOrderId) {
        currentOrderId = await createOrder();
        if (!currentOrderId) {
          setLoading(false);
          return;
        }
      }

      const response = await axios.post(`${API}/checkout/stripe`, {
        order_id: currentOrderId,
        origin_url: window.location.origin,
        payment_method: 'stripe'
      });

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePayPalCreateOrder = async () => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    let currentOrderId = orderId;
    if (!currentOrderId) {
      if (!validateForm()) return;
      currentOrderId = await createOrder();
      if (!currentOrderId) return;
    }

    try {
      const response = await axios.post(`${API}/checkout/paypal/create`, {
        order_id: currentOrderId,
        origin_url: window.location.origin
      });
      return response.data.order_id;
    } catch (error) {
      console.error('PayPal create order error:', error);
      toast.error('Failed to create PayPal order');
      return;
    }
  };

  const handlePayPalApprove = async (data, actions) => {
    try {
      // For sandbox, we simulate capture
      const response = await axios.post(`${API}/checkout/paypal/capture`, {
        order_id: orderId,
        paypal_order_id: data.orderID
      });

      clearCart();
      toast.success('Payment successful!');
      navigate(`/order-confirmation?order_number=${response.data.order_number}`);
    } catch (error) {
      console.error('PayPal capture error:', error);
      toast.error('Payment failed. Please try again.');
    }
  };

  if (cartItems.length === 0 && !orderId) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center py-20">
            <ShoppingBag className="w-16 h-16 text-[#E3DBD1] mx-auto mb-4" />
            <h1 className="text-2xl font-heading text-[#2A1A14] mb-4">Your cart is empty</h1>
            <Button
              onClick={() => navigate('/')}
              className="bg-[#7B3E19] hover:bg-[#633113] text-white rounded-sm"
            >
              Continue Shopping
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] pt-28 pb-16" data-testid="checkout-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 text-[#786C65] hover:text-[#2A1A14]"
          data-testid="back-to-shop"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Shop
        </Button>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-3xl md:text-4xl font-heading font-semibold text-[#2A1A14] mb-8"
        >
          Checkout
        </motion.h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Shipping Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2"
          >
            <Card className="border-[#E3DBD1] bg-white rounded-sm">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-[#2A1A14]">
                  Shipping Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="customer_name" className="text-[#2A1A14]">Full Name *</Label>
                    <Input
                      id="customer_name"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleInputChange}
                      className={`mt-1 border-[#E3DBD1] focus:ring-[#7B3E19] ${errors.customer_name ? 'border-red-500' : ''}`}
                      data-testid="input-customer-name"
                    />
                    {errors.customer_name && <p className="text-red-500 text-sm mt-1">{errors.customer_name}</p>}
                  </div>
                  <div>
                    <Label htmlFor="customer_email" className="text-[#2A1A14]">Email *</Label>
                    <Input
                      id="customer_email"
                      name="customer_email"
                      type="email"
                      value={formData.customer_email}
                      onChange={handleInputChange}
                      className={`mt-1 border-[#E3DBD1] focus:ring-[#7B3E19] ${errors.customer_email ? 'border-red-500' : ''}`}
                      data-testid="input-customer-email"
                    />
                    {errors.customer_email && <p className="text-red-500 text-sm mt-1">{errors.customer_email}</p>}
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone" className="text-[#2A1A14]">Phone (Optional)</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="mt-1 border-[#E3DBD1] focus:ring-[#7B3E19]"
                    data-testid="input-phone"
                  />
                </div>

                <div>
                  <Label htmlFor="shipping_address" className="text-[#2A1A14]">Street Address *</Label>
                  <Input
                    id="shipping_address"
                    name="shipping_address"
                    value={formData.shipping_address}
                    onChange={handleInputChange}
                    className={`mt-1 border-[#E3DBD1] focus:ring-[#7B3E19] ${errors.shipping_address ? 'border-red-500' : ''}`}
                    data-testid="input-shipping-address"
                  />
                  {errors.shipping_address && <p className="text-red-500 text-sm mt-1">{errors.shipping_address}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="shipping_city" className="text-[#2A1A14]">City *</Label>
                    <Input
                      id="shipping_city"
                      name="shipping_city"
                      value={formData.shipping_city}
                      onChange={handleInputChange}
                      className={`mt-1 border-[#E3DBD1] focus:ring-[#7B3E19] ${errors.shipping_city ? 'border-red-500' : ''}`}
                      data-testid="input-shipping-city"
                    />
                    {errors.shipping_city && <p className="text-red-500 text-sm mt-1">{errors.shipping_city}</p>}
                  </div>
                  <div>
                    <Label htmlFor="shipping_state" className="text-[#2A1A14]">State *</Label>
                    <Input
                      id="shipping_state"
                      name="shipping_state"
                      value={formData.shipping_state}
                      onChange={handleInputChange}
                      className={`mt-1 border-[#E3DBD1] focus:ring-[#7B3E19] ${errors.shipping_state ? 'border-red-500' : ''}`}
                      data-testid="input-shipping-state"
                    />
                    {errors.shipping_state && <p className="text-red-500 text-sm mt-1">{errors.shipping_state}</p>}
                  </div>
                  <div>
                    <Label htmlFor="shipping_zip" className="text-[#2A1A14]">ZIP Code *</Label>
                    <Input
                      id="shipping_zip"
                      name="shipping_zip"
                      value={formData.shipping_zip}
                      onChange={handleInputChange}
                      className={`mt-1 border-[#E3DBD1] focus:ring-[#7B3E19] ${errors.shipping_zip ? 'border-red-500' : ''}`}
                      data-testid="input-shipping-zip"
                    />
                    {errors.shipping_zip && <p className="text-red-500 text-sm mt-1">{errors.shipping_zip}</p>}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Methods */}
            <Card className="border-[#E3DBD1] bg-white rounded-sm mt-6">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-[#2A1A14]">
                  Payment Method
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Stripe Button */}
                <Button
                  onClick={handleStripeCheckout}
                  disabled={loading}
                  className="w-full bg-[#7B3E19] hover:bg-[#633113] text-white rounded-sm py-6 text-lg"
                  data-testid="stripe-checkout-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay with Card (Google Pay / Apple Pay)
                    </>
                  )}
                </Button>

                <div className="relative">
                  <Separator className="my-4" />
                  <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4 text-sm text-[#786C65]">
                    or
                  </span>
                </div>

                {/* PayPal Button */}
                <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: 'USD' }}>
                  <div className="paypal-button-container" data-testid="paypal-button-container">
                    <PayPalButtons
                      style={{ layout: 'horizontal', tagline: false }}
                      createOrder={handlePayPalCreateOrder}
                      onApprove={handlePayPalApprove}
                      onError={(err) => {
                        console.error('PayPal error:', err);
                        toast.error('PayPal payment failed');
                      }}
                    />
                  </div>
                </PayPalScriptProvider>
              </CardContent>
            </Card>
          </motion.div>

          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="border-[#E3DBD1] bg-white rounded-sm sticky top-28">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-[#2A1A14]">
                  Order Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {cartItems.map((item) => (
                    <div key={item.product_id} className="flex gap-3" data-testid={`summary-item-${item.product_id}`}>
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded-sm"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#2A1A14] truncate">
                          {item.name.split(' - ')[0]}
                        </p>
                        <p className="text-sm text-[#786C65]">
                          Qty: {item.quantity}
                        </p>
                        <p className="text-sm font-semibold text-[#7B3E19]">
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <Separator className="my-4" />

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#786C65]">Subtotal</span>
                    <span className="text-[#2A1A14]">${cartTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#786C65]">Shipping</span>
                    <span className="text-[#2A1A14]">Calculated at checkout</span>
                  </div>
                </div>

                <Separator className="my-4" />

                <div className="flex justify-between">
                  <span className="font-heading font-semibold text-[#2A1A14]">Total</span>
                  <span className="font-heading text-xl font-semibold text-[#7B3E19]" data-testid="checkout-total">
                    ${cartTotal.toFixed(2)}
                  </span>
                </div>

                {/* Allergen Notice */}
                <div className="mt-4 p-3 bg-[#EAE3D9] rounded-sm" data-testid="checkout-allergen-notice">
                  <p className="text-xs text-[#786C65]">
                    <span className="font-semibold">Allergen Notice:</span> Our products contain dairy and may also contain or have been prepared alongside nuts and/or nut oils.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};
